import { Button } from '@/components/ui/shadcn/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/shadcn/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { Calculator, Check, X } from 'lucide-react';
import { useState } from 'react';

export interface CalculationRequestOption {
    id: string;
    request_number?: string | null;
    object_name?: string | null;
    company_name?: string | null;
    status?: string | null;
    lead_id?: string | null;
    project_id?: string | null;
    /** Caller-supplied hint shown under the option, e.g. "Attached to another lead". */
    note?: string | null;
}

interface CalculationRequestSelectBase {
    /** The candidate list. The caller decides what is eligible; the server validates the same set. */
    requests: CalculationRequestOption[];
    disabled?: boolean;
    className?: string;
    /**
     * 'field'  — fills the width of a form row, always bordered.
     * 'inline' — full width, but drops the border and background once a selection
     *            exists so it reads as a value rather than an input. Keeps the
     *            chrome while empty so it is still discoverable.
     */
    variant?: 'field' | 'inline';
    /** Copy overrides; each falls back to a generic `common.calculation_request_select.*` string. */
    labels?: {
        placeholder?: string;
        search?: string;
        empty?: string;
        heading?: string;
        clear?: string;
        noneOption?: string;
        /** Multi mode only: shown as "3 {selected}" past `maxLabels`. */
        selected?: string;
        /**
         * Pins the trigger to this text whatever is selected — for action-style triggers
         * ("Attach existing") whose selection is already visible elsewhere on the page.
         */
        trigger?: string;
    };
}

export interface CalculationRequestSelectSingleProps extends CalculationRequestSelectBase {
    multiple?: false;
    value: string | null;
    onValueChange: (value: string | null) => void;
    /** Render the inline × that clears the selection. */
    allowClear?: boolean;
    /**
     * Offer an explicit "Not linked" entry at the top of the list. Preferred over the ×
     * where the trigger has no room for it, e.g. the inline variant.
     */
    showNoneOption?: boolean;
}

export interface CalculationRequestSelectMultipleProps extends CalculationRequestSelectBase {
    multiple: true;
    values: string[];
    onValuesChange: (values: string[]) => void;
    /** How many request numbers to spell out on the trigger before collapsing to a count. */
    maxLabels?: number;
}

export type CalculationRequestSelectProps = CalculationRequestSelectSingleProps | CalculationRequestSelectMultipleProps;

function requestLabel(request: CalculationRequestOption | null | undefined): string {
    if (!request) {
        return '';
    }

    return request.request_number?.trim() || request.id;
}

function requestSubtitle(request: CalculationRequestOption): string {
    return [request.object_name, request.company_name].filter(Boolean).join(' · ');
}

/**
 * Calculation-request picker, built to the same contract as PersonSelect: a
 * searchable popover over a caller-supplied list, controlled, single or multiple.
 *
 * Used on the lead page (many calculations to one lead) and the project page (one
 * calculation to one project).
 */
export function CalculationRequestSelect(props: CalculationRequestSelectProps) {
    const { requests, disabled = false, className, variant = 'inline', labels } = props;
    const isMultiple = props.multiple === true;

    const { t } = useTranslations();
    const [open, setOpen] = useState(false);

    const placeholder = labels?.placeholder ?? t('common.calculation_request_select.none', 'Not linked');
    const searchLabel = labels?.search ?? t('common.calculation_request_select.search', 'Search calculations…');
    const emptyLabel = labels?.empty ?? t('common.calculation_request_select.empty', 'No calculation requests found.');
    const heading = labels?.heading ?? t('common.calculation_request_select.heading', 'Calculation requests');
    const clearLabel = labels?.clear ?? t('common.calculation_request_select.clear', 'Clear selection');
    const noneOptionLabel = labels?.noneOption ?? placeholder;
    const selectedWord = labels?.selected ?? t('common.calculation_request_select.selected', 'selected');

    // Selection state, normalised so the render path below is mode-agnostic.
    const singleValue = isMultiple ? null : (props as CalculationRequestSelectSingleProps).value;
    const multiValues = isMultiple ? (props as CalculationRequestSelectMultipleProps).values : [];
    const selectedIds = isMultiple ? multiValues : singleValue ? [singleValue] : [];

    const selected = singleValue ? (requests.find((request) => request.id === singleValue) ?? null) : null;
    const selectedRequests = isMultiple ? requests.filter((request) => multiValues.includes(request.id)) : [];
    const hasSelection = selectedIds.length > 0;

    const select = (id: string) => {
        if (isMultiple) {
            const { onValuesChange } = props as CalculationRequestSelectMultipleProps;
            // Stay open: attaching several calculations in a row is the point of multi mode.
            onValuesChange(multiValues.includes(id) ? multiValues.filter((current) => current !== id) : [...multiValues, id]);

            return;
        }

        const { onValueChange } = props as CalculationRequestSelectSingleProps;
        onValueChange(id === singleValue ? null : id);
        setOpen(false);
    };

    const allowClear = isMultiple ? false : ((props as CalculationRequestSelectSingleProps).allowClear ?? true);
    const showNoneOption = isMultiple ? false : ((props as CalculationRequestSelectSingleProps).showNoneOption ?? false);
    const maxLabels = isMultiple ? ((props as CalculationRequestSelectMultipleProps).maxLabels ?? 2) : 0;

    /** Single mode only — both call sites are already gated on !isMultiple. */
    const clearSingle = () => {
        if (!isMultiple) {
            (props as CalculationRequestSelectSingleProps).onValueChange(null);
        }
    };

    const showClear = allowClear && selected !== null && !disabled;
    // Inline drops its chrome once filled so the value reads as text, not an input.
    const chromeless = variant === 'inline' && hasSelection;

    const pinnedTriggerLabel = labels?.trigger;
    const triggerLabel =
        pinnedTriggerLabel ??
        (isMultiple
            ? selectedRequests.length <= maxLabels
                ? selectedRequests.map(requestLabel).join(', ')
                : `${selectedRequests.length} ${selectedWord}`
            : requestLabel(selected));

    const renderItem = (request: CalculationRequestOption) => {
        const subtitle = requestSubtitle(request);

        return (
            <CommandItem
                key={request.id}
                value={`${requestLabel(request)} ${subtitle} ${request.id}`}
                onSelect={() => select(request.id)}
                className="items-start gap-2"
            >
                <Calculator className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-medium">{requestLabel(request)}</span>
                    {subtitle ? <span className="text-muted-foreground truncate text-xs">{subtitle}</span> : null}
                    {request.note ? <span className="text-muted-foreground truncate text-xs italic">{request.note}</span> : null}
                </span>
                {selectedIds.includes(request.id) ? <Check className="text-primary mt-0.5 h-4 w-4 shrink-0" /> : null}
            </CommandItem>
        );
    };

    return (
        // Relative so the clear affordance can sit inside the field's right edge rather
        // than beside it. A button can't nest in a button, so the clear control is an
        // absolutely-positioned sibling overlaying the trigger.
        <div className={cn('relative flex w-full items-center', className)}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant={chromeless ? 'ghost' : 'outline'}
                        role="combobox"
                        aria-expanded={open}
                        disabled={disabled}
                        className={cn(
                            'group h-auto w-full justify-start gap-2 py-1.5 font-normal',
                            // Filled reads as a value, so it sits flush with the label above
                            // it rather than indented like a control.
                            hasSelection || pinnedTriggerLabel ? 'pl-0' : 'pl-2',
                            // Leave room for the clear control so the label never runs under it.
                            showClear && 'pr-9',
                            !hasSelection && !pinnedTriggerLabel && 'text-muted-foreground',
                        )}
                    >
                        {/*
                         * Once a calculation is picked the trigger reads as the value, not
                         * as a control: no icon, and the number styled like the linked-lead
                         * link beside it. It stays a button, so it is still changeable.
                         * The icon remains while empty, where it is the only affordance.
                         */}
                        {hasSelection || pinnedTriggerLabel ? null : <Calculator className="text-muted-foreground/60 h-4 w-4 shrink-0" />}
                        <span
                            className={cn(
                                'truncate text-sm',
                                (hasSelection || pinnedTriggerLabel) && 'text-primary underline-offset-4 group-hover:underline',
                            )}
                        >
                            {hasSelection || pinnedTriggerLabel ? triggerLabel : placeholder}
                        </span>
                    </Button>
                </PopoverTrigger>

                {/* collisionPadding keeps the panel off the viewport edge when the trigger
                    sits far right, as it does on the lead tab's card header. */}
                <PopoverContent className="w-80 p-0" align="start" collisionPadding={8}>
                    <Command>
                        <CommandInput placeholder={searchLabel} />
                        <CommandList>
                            <CommandEmpty>{emptyLabel}</CommandEmpty>

                            {showNoneOption ? (
                                <CommandGroup>
                                    <CommandItem
                                        value={noneOptionLabel}
                                        onSelect={() => {
                                            clearSingle();
                                            setOpen(false);
                                        }}
                                        className="gap-2"
                                    >
                                        <X className="text-muted-foreground h-4 w-4 shrink-0" />
                                        <span className="text-muted-foreground min-w-0 flex-1 truncate text-sm">{noneOptionLabel}</span>
                                        {selected === null ? <Check className="text-primary h-4 w-4 shrink-0" /> : null}
                                    </CommandItem>
                                </CommandGroup>
                            ) : null}

                            <CommandGroup heading={heading}>{requests.map(renderItem)}</CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>

            {showClear ? (
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    // stopPropagation so clearing does not also open the popover.
                    onClick={(event) => {
                        event.stopPropagation();
                        clearSingle();
                    }}
                    className="text-muted-foreground hover:text-foreground absolute top-1/2 right-1.5 h-6 w-6 -translate-y-1/2"
                    aria-label={clearLabel}
                >
                    <X className="h-3.5 w-3.5" />
                </Button>
            ) : null}
        </div>
    );
}
